import { createEstimateEmailService, ConsoleEmailTransport, type EmailMessage, type EmailTransport, type EstimateEmailService } from "@reno-estimator/email";

// `window.RenoEstimatorConfig`'s type is declared once, in
// estimatorClient.ts (RenoEstimatorConfig), and reused here - same
// pattern analyticsClient.ts and PrivacyPolicyLink.tsx already use.
function readHostConfig() {
  return typeof window !== "undefined" ? window.RenoEstimatorConfig : undefined;
}

/**
 * Phase 23 fix (Full Application Red-Team Audit, Finding 1 - Critical):
 * the real `EmailTransport` this file's own doc comment said was
 * "most importantly" still missing. Posts the already-built email
 * (subject/text/html, composed client-side by
 * @reno-estimator/email's content.ts, unchanged) to this plugin's
 * `/estimate-email` REST route, which sends it via WordPress's own
 * `wp_mail()` - see class-leads-rest.php. The route deliberately ignores
 * any `from`/`replyTo` this transport sends and substitutes the site's
 * own configured addresses server-side (a client-supplied From/Reply-To
 * on outgoing mail is a spoofing surface, not a feature).
 */
export class HttpEmailTransport implements EmailTransport {
  constructor(private readonly restUrl: string, private readonly nonce?: string) {}

  async send(message: EmailMessage): Promise<void> {
    const response = await fetch(`${this.restUrl.replace(/\/$/, "")}/estimate-email`, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        ...(this.nonce ? { "X-WP-Nonce": this.nonce } : {})
      },
      body: JSON.stringify({ to: message.to, subject: message.subject, text: message.text, html: message.html })
    });
    if (!response.ok) {
      throw new Error(`Estimate email send failed with status ${response.status}`);
    }
  }
}

/**
 * The one place this app wires up an EstimateEmailService, mirroring
 * estimatorClient.ts's pattern for the calculation engine: components
 * only ever depend on the `EstimateEmailService` interface. Picks
 * `HttpEmailTransport` automatically whenever `RenoEstimatorConfig.restUrl`
 * is present (a WordPress host - see leadStore.ts's identical pattern for
 * leads), falling back to `ConsoleEmailTransport` (never actually sends -
 * logs a redacted line) for standalone/dev/test builds - see
 * `isEmailDeliveryConfigured()` below, which the UI uses so it never
 * claims an email is on its way when nothing can actually send one.
 */
let cachedService: EstimateEmailService | null = null;

export function getEstimateEmailService(): EstimateEmailService {
  if (!cachedService) {
    const config = readHostConfig();
    const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://example.com";
    const transport = config?.restUrl ? new HttpEmailTransport(config.restUrl, config.wpRestNonce) : new ConsoleEmailTransport();
    cachedService = createEstimateEmailService(
      {
        // A real deployment's verified sending address is set server-side
        // by the WordPress route itself (see class-leads-rest.php) when
        // HttpEmailTransport is in use; this "from" is only ever actually
        // used by ConsoleEmailTransport's local-dev logging.
        fromAddress: "estimates@example.com",
        siteUrl
      },
      transport
    );
  }
  return cachedService;
}

/**
 * Whether "Email Me My Estimate" will actually deliver an email. False in
 * standalone/dev/test builds. See LeadCaptureForm.tsx/EmailEstimateForm.tsx.
 */
export function isEmailDeliveryConfigured(): boolean {
  return Boolean(readHostConfig()?.restUrl);
}
